import { Entity, FieldType, PersistenceType } from 'gossamer-unified';
import { Event } from './Event';
import { Institution } from './Institution';
import { UserView } from './UserView';
import {
  formatEventSeriesReconciliationOperation,
  formatEventSeriesReconciliationOutcome,
  formatEventSeriesReconciliationScope,
} from './EventRepetition';

export {
  formatEventSeriesReconciliationOperation,
  formatEventSeriesReconciliationOutcome,
  formatEventSeriesReconciliationScope,
} from './EventRepetition';

export type EventSeriesReconciliationOperation =
  | 'pattern-apply'
  | 'series-split'
  | 'recurring-edit'
  | 'recurring-cancellation';

export type EventSeriesReconciliationScope = 'this-and-future' | 'entire-series';

export interface EventSeriesReconciliationAuditRow {
  id: number;
  repeat_group_id: string;
  root_event_id: number | null;
  selected_event_id: number | null;
  institution_id: number;
  operation_type: EventSeriesReconciliationOperation;
  scope: EventSeriesReconciliationScope;
  effective_from: string | null;
  include_exceptions: boolean;
  proposed_pattern: Record<string, unknown> | null;
  outcome_summary: Record<string, unknown>;
  affected_event_ids: number[];
  performed_by: string;
  performed_at: string;
}

export const EventSeriesReconciliationAudit: Entity<EventSeriesReconciliationAuditRow> = {
  id: 'EventSeriesReconciliationAudit',
  title: 'Series Reconciliation Audit',
  service: 'main',
  rowLabel: (row) => formatEventSeriesReconciliationOperation(row.operation_type),
  primaryKeyFields: ['id'],
  source: { tableName: 'public.event_series_reconciliation_audit' },
  security: [
    {
      role: 'admin',
      action: 'read',
      access: 'conditional',
      condition: 'institution_id = {adminInstitutionId}',
    },
    { role: 'staff', action: 'read', access: 'all' },
  ],
  defaultSort: [['performed_at', 'DESC']],
  defaultFilters: [],
  fields: {
    id: {
      seq: 5,
      type: FieldType.Integer,
      label: 'Id',
      autoIncrement: true,
      persistence: PersistenceType.BackEndManaged,
    },
    repeat_group_id: {
      seq: 10,
      type: FieldType.Text,
      label: 'Repeat Group',
      persistence: PersistenceType.BackEndManaged,
    },
    root_event_id: {
      seq: 15,
      type: FieldType.Reference,
      label: 'Root Event',
      refEntity: Event,
      persistence: PersistenceType.BackEndManaged,
    },
    selected_event_id: {
      seq: 20,
      type: FieldType.Reference,
      label: 'Selected Event',
      refEntity: Event,
      persistence: PersistenceType.BackEndManaged,
    },
    institution_id: {
      seq: 25,
      type: FieldType.Reference,
      label: 'Institution',
      refEntity: Institution,
      persistence: PersistenceType.BackEndManaged,
    },
    operation_type: {
      seq: 30,
      type: FieldType.Options,
      label: 'Operation',
      listColumn: true,
      options: [
        { id: 'pattern-apply', label: 'Repeat pattern updated' },
        { id: 'series-split', label: 'Series split' },
        { id: 'recurring-edit', label: 'Series details updated' },
        { id: 'recurring-cancellation', label: 'Series cancellation' },
      ],
      persistence: PersistenceType.BackEndManaged,
    },
    scope: {
      seq: 35,
      type: FieldType.Options,
      label: 'Scope',
      options: [
        { id: 'this-and-future', label: 'This and future events' },
        { id: 'entire-series', label: 'Entire series' },
      ],
      persistence: PersistenceType.BackEndManaged,
    },
    effective_from: {
      seq: 40,
      type: FieldType.Datetime,
      label: 'Effective From',
      persistence: PersistenceType.BackEndManaged,
    },
    include_exceptions: {
      seq: 45,
      type: FieldType.Boolean,
      label: 'Included Exceptions',
      persistence: PersistenceType.BackEndManaged,
    },
    proposed_pattern: {
      seq: 50,
      type: FieldType.Object,
      label: 'Proposed Pattern',
      persistence: PersistenceType.BackEndManaged,
    },
    outcome_summary: {
      seq: 55,
      type: FieldType.Object,
      label: 'Outcome Summary',
      persistence: PersistenceType.BackEndManaged,
    },
    affected_event_ids: {
      seq: 60,
      type: FieldType.Object,
      label: 'Affected Event IDs',
      persistence: PersistenceType.BackEndManaged,
    },
    performed_by: {
      seq: 65,
      type: FieldType.Reference,
      label: 'Performed By',
      refEntity: UserView,
      persistence: PersistenceType.BackEndManaged,
    },
    performed_at: {
      seq: 70,
      type: FieldType.Datetime,
      label: 'Performed At',
      listColumn: true,
      persistence: PersistenceType.BackEndManaged,
    },
  },
};
